import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import "./Combobox.css";

export default function Combobox({
  value,
  onChange,
  options,
  placeholder = "",
  getLabel,
  getSearchText,
  renderOption,
  maxResults = 12,
  allowCustom = false,
}) {
  const listboxId = useId();
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => o.value === value) ?? null;
  const selectedLabel = selected ? getLabel(selected) : allowCustom ? (value ?? "") : "";

  const [query, setQuery] = useState(selectedLabel);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        if (allowCustom) {
          const typed = query.trim();
          if (typed && typed !== selectedLabel) onChange(typed);
        } else {
          setQuery(selectedLabel);
        }
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [selectedLabel, allowCustom, query, onChange]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!open) return [];
    if (!q) return options.slice(0, maxResults);
    return options.filter((o) => getSearchText(o).toLowerCase().includes(q)).slice(0, maxResults);
  }, [query, options, open, getSearchText, maxResults]);

  const commit = (option) => {
    onChange(option.value);
    setQuery(getLabel(option));
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (open && filtered[activeIndex]) {
        e.preventDefault();
        commit(filtered[activeIndex]);
      } else if (allowCustom && query.trim()) {
        e.preventDefault();
        onChange(query.trim());
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(selectedLabel);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="trunfo-combobox" ref={rootRef}>
      <input
        ref={inputRef}
        type="text"
        className="trunfo-combobox__input"
        value={query}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        autoComplete="off"
        onFocus={() => {
          setOpen(true);
          setActiveIndex(0);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(0);
          if (e.target.value === "") onChange("");
        }}
        onKeyDown={handleKeyDown}
      />
      {open && filtered.length > 0 ? (
        <ul className="trunfo-combobox__list" id={listboxId} role="listbox">
          {filtered.map((option, i) => (
            <li
              key={option.value}
              role="option"
              aria-selected={i === activeIndex}
              className={
                "trunfo-combobox__option" +
                (i === activeIndex ? " trunfo-combobox__option--active" : "") +
                (option.value === value ? " trunfo-combobox__option--selected" : "")
              }
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => commit(option)}
            >
              {renderOption ? renderOption(option) : getLabel(option)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
